<template>
    <div class="column is-full-mobile is-sidebar-menu is-narrow is-relative is-app-sidebar tw:h-full">
        <aside v-if="hasMenu" id="application_sidebar" class="menu">
            <p v-if="isCourseTemplate" class="m-b-sm m-t-md" :class="[summarize ? 'has-text-centered' : '']">
                <em v-if="!summarize" class="m-r-smd">Preview Menu</em>

                <e-tooltip
                    animated
                    multilined
                    size="is-large"
                    label="This is a preview of the menu that enrolled students will see in their dashboard. As you make changes to the course template this menu will update."
                    position="is-right"
                >
                    <e-icon icon="info-circle" type="is-primary" size="is-small" />
                </e-tooltip>
            </p>

            <!--            TODO: When search is supported, re-enable this -->
            <!--            <div class="topbar-search" :class="{ 'topbar-search-active': searchActive }">-->
            <!--                <Button severity="secondary" rounded type="button" icon="pi pi-search" @click="activateSearch" />-->
            <!--                <div class="search-input-wrapper">-->
            <!--                    <IconField>-->
            <!--                        <InputText class="searchInput" type="text" placeholder="Search" @blur="deactivateSearch" @keydown.escape="deactivateSearch" />-->
            <!--                        <InputIcon class="pi pi-search" />-->
            <!--                    </IconField>-->
            <!--                </div>-->
            <!--            </div>-->

            <nav v-if="menu" class="is-relative">
                <div v-if="summarize" @click="toggleSummary" />

                <app-sidebar-summary
                    v-if="summarize"
                    :items="menu"
                    :has-notes-menu="!isCourseTemplate"
                    :course-id="courseId"
                    @open="toggleSummary"
                />

                <div v-else class="course-menu-container">
                    <course-template-menu v-if="isCourseTemplate" :course-template-id="courseId" :items="menu" />
                    <course-menu v-else :item-to-open="itemToOpen" :course-id="courseId" :items="menu" mode="sidebar" />
                </div>
            </nav>
        </aside>

        <div class="is-app-sidebar-meta tw:flex tw:flex-col">
            <brand-icon class="tw:mx-auto" />

            <p>{{ appVersion }}</p>
        </div>
    </div>
</template>

<script>
import get from 'lodash/get.js';
import { createNamespacedHelpers } from 'vuex';
import AppSidebarSummary from '@/components/app/sidebar/AppSidebarSummary.vue';
import BrandIcon from '@/components/elements/BrandIcon.vue';
import CourseMenu from '@/components/nested-list/CourseMenu.vue';
import CourseTemplateMenu from '@/components/nested-list/CourseTemplateMenu.vue';
import { useLayout } from '@/layouts/composables/layout.js';
import Course from '@/models/Course.js';
import CourseTemplate from '@/models/CourseTemplate.js';

const { mapGetters } = createNamespacedHelpers('Menu');

export default {
    name: 'AppSidebar',

    components: {
        CourseTemplateMenu,
        AppSidebarSummary,
        CourseMenu,
        BrandIcon,
    },

    emits: ['update:expanded'],

    setup() {
        const { layoutState, toggleSidebarSummary } = useLayout();

        return {
            layoutState,
            toggleSidebarSummary,
        };
    },

    // TODO: When search is supported, re-enable this
    // const searchActive = ref(false);
    data: () => ({
        appVersion: window.appVersion,
        openCourseLog: false,
        syncSortedItems: null,
        itemToOpen: undefined,
    }),

    computed: {
        ...mapGetters(['menu', 'hasMenu']),

        summarize() {
            // Summary (icon rail) is a desktop affordance; the mobile/tablet
            // drawer is wide and always shows the full expanded menu.
            if (this.$isMobile || this.$isTablet) {
                return false;
            }
            return this.layoutState.sidebarSummaryMode;
        },

        authUser() {
            return this.$store.getters['Auth/user'];
        },

        courseId() {
            const id = get(this.$route.params, 'courseId', get(this.$route.params, 'id', null));

            if (!id) return null;

            return Number.parseInt(id, 10);
        },

        isCourseTemplate() {
            return this.$route.name?.includes('template');
        },

        course() {
            if (!this.courseId) {
                return null;
            }

            return Course.find(this.courseId);
        },

        hasMenu() {
            return this.$store.getters['Menu/hasMenu'] || CourseTemplate.find(this.courseId)?.menu();
        },

        menu() {
            return this.isCourseTemplate
                ? CourseTemplate.find(this.courseId)?.menu()
                : this.$store.getters['Menu/menu'];
        },
    },

    watch: {
        summarize(newValue) {
            // Emit update when summary state changes (including from topbar button)
            this.$emit('update:expanded', !newValue);
        },
    },

    mounted() {
        // Emit initial state
        this.$emit('update:expanded', !this.summarize);
    },

    methods: {
        // TODO: When search is supported, re-enable this
        // function activateSearch() {
        //     searchActive.value = true;
        //
        //     setTimeout(() => {
        //         const input = document.querySelector('.searchInput');
        //         input.focus();
        //     }, 100);
        // }
        //
        // function deactivateSearch() {
        //     searchActive.value = false;
        // }
        toggleSummary(item) {
            this.itemToOpen = this.toggleSidebarSummary(item);
            this.$emit('update:expanded', !this.summarize);
        },

        resetToSummary() {
            // Reset to summary view (collapsed state)
            if (!this.summarize) {
                this.toggleSidebarSummary();
            }
            this.itemToOpen = undefined;
            this.$emit('update:expanded', false);
        },
    },
};
</script>

<style lang="scss">
@import '~assets/scss/toolbox.scss';

.is-app-sidebar {
    display: flex !important;
    flex-direction: column;
    background-color: transparent !important;

    #application_sidebar {
        background-color: transparent !important;
    }

    &-meta {
        margin-top: auto;
        padding-top: $size-5;
        text-align: center;

        .brandIcon {
            margin-bottom: $size-10;
            vertical-align: bottom;
        }

        p {
            font-size: $very-small-font-size;
            line-height: 1;
        }
    }
}

.course-menu-container {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    overflow-x: hidden;
    padding: 0;
    margin: 0;
}
</style>
